import {
  Activity,
  ArrowRight,
  Database,
  GitPullRequest,
  MessageSquare,
  ReceiptText,
  TicketCheck,
} from "lucide-react"
import type { Metadata } from "next"
import Link from "next/link"

export const metadata: Metadata = {
  title: "Integrations",
  description:
    "Typed, least-privilege connector boundaries for Resolto investigations and actions.",
}

const connectors = [
  {
    name: "Slack",
    role: "Intake and threaded updates",
    detail: "DMs, mentions, shortcuts, modals, feedback, and approval controls.",
    mode: "Adapter foundation",
    icon: MessageSquare,
  },
  {
    name: "PostgreSQL",
    role: "Read-only application evidence",
    detail: "Administrator-authored parameterised query templates. Never model-generated SQL.",
    mode: "Demo adapter",
    icon: Database,
  },
  {
    name: "Sentry",
    role: "Issues, events, and releases",
    detail: "Read-only issue context, event summaries, stack traces, tags, and release metadata.",
    mode: "Demo adapter",
    icon: Activity,
  },
  {
    name: "Stripe",
    role: "Billing evidence",
    detail: "Read customers, subscriptions, payments, invoices, and relevant webhook metadata.",
    mode: "Demo adapter",
    icon: ReceiptText,
  },
  {
    name: "Linear and Jira",
    role: "Complete developer escalation",
    detail: "Create or update evidence-backed technical cases through controlled writes.",
    mode: "Contract boundary",
    icon: TicketCheck,
  },
  {
    name: "GitHub App",
    role: "Bounded code proposals",
    detail:
      "Selected repositories and paths, branch and pull request only—no merge or deployment scope.",
    mode: "Demo proposal",
    icon: GitPullRequest,
  },
]

export default function IntegrationsPage() {
  return (
    <main id="main-content">
      <section className="marketing-page-hero">
        <div className="marketing-container">
          <p className="section-kicker">Integrations</p>
          <h1>Connect the systems that hold the evidence—not unrestricted access.</h1>
          <p>
            Every connector declares its capabilities, scopes, read and write operations, risk,
            timeouts, limits, retry policy, PII class, audit fields, and health.
          </p>
        </div>
      </section>
      <section className="marketing-section integrations-section">
        <div className="marketing-container integration-list">
          {connectors.map((connector) => {
            const Icon = connector.icon
            return (
              <article key={connector.name}>
                <Icon aria-hidden="true" />
                <div>
                  <h2>{connector.name}</h2>
                  <strong>{connector.role}</strong>
                  <p>{connector.detail}</p>
                </div>
                <span>{connector.mode}</span>
              </article>
            )
          })}
        </div>
        <div className="marketing-container truth-note">
          <strong>Integration truth</strong>
          <p>
            The working product uses realistic demo adapters until external credentials and scopes
            are configured. A connector is never presented as operational unless its health check
            and contract tests pass.
          </p>
        </div>
      </section>
      <section className="marketing-section compact-cta-section">
        <div className="marketing-container compact-cta">
          <div>
            <h2>Start with one narrow stack.</h2>
            <p>
              Slack, PostgreSQL, Sentry, Stripe, and Linear or Jira are the focused launch path.
            </p>
          </div>
          <Link className="button button-primary" href="/contact">
            Scope your systems
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  )
}
